import React, { useEffect, useState } from "react";
import { BiPencil } from "react-icons/bi";
import { FaUserCircle } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { selectUser } from "../../redux/authSlice";
import { useSelector } from "react-redux";
import { useUpdateProfileMutation } from "../../redux/userApi";
import { toast } from "react-toastify";
import ButtonLoading from "../Button loading/ButtonLoading";
import { showErrorToast } from "../../utils/Utils";

const UpdateProfileForm = ({ isOpen, setIsOpen }) => {
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const [phone, setPhone] = useState("");
  const [cnic, setCnic] = useState("");
  const [resetTrigger, setResetTrigger] = useState(0);
  const user = useSelector(selectUser);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    gender: "",
  });

  const [formDataError, setFormDataError] = useState({
    cnic: "",
    phone: "",
  });

  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        email: user?.email,
        name: user?.name,
        role: user?.role,
        address: user?.address,
        gender: user?.gender,
      });

      setPhone(user?.phone);
      setCnic(user?.cnic);

      if (user?.profile) {
        setImage({
          src: user?.profile,
          file: null,
        });
      }
    }
  }, [user, isOpen, resetTrigger]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFile = (file) => {
    setError("");

    if (!file) {
      setError("No file selected.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      if (img.width !== img.height || img.width > 1000) {
        setError("Image must be square and no larger than 1000x1000 pixels.");
        URL.revokeObjectURL(objectUrl);
        return;
      }

      const newImage = {
        src: objectUrl,
        file,
      };

      setImage(newImage);
    };

    img.onerror = () => {
      setError("Invalid image file.");
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const formatPhone = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");

    // Ensure it starts with 92
    let formatted = digits;
    if (formatted.startsWith("0")) {
      formatted = "92" + formatted.slice(1);
    } else if (!formatted.startsWith("92")) {
      formatted = "92" + formatted;
    }

    // Limit to 12 digits (92 + 10 digits)
    formatted = formatted.slice(0, 12);

    // Apply formatting: +92 300-1234567
    let result = "+";
    if (formatted.length >= 2) result += formatted.slice(0, 2); // +92
    if (formatted.length >= 5) result += " " + formatted.slice(2, 5); // +92 300
    if (formatted.length > 5) result += "-" + formatted.slice(5); // +92 300-1234567

    return result;
  };

  const handlePhoneChange = (e) => {
    const rawValue = e.target.value;
    const formatted = formatPhone(rawValue);
    setPhone(formatted);
    setError("");
  };

  const formatCnic = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 13); // max 13 digits
    let formatted = "";

    if (digits.length <= 5) {
      formatted = digits;
    } else if (digits.length <= 12) {
      formatted = `${digits.slice(0, 5)}-${digits.slice(5)}`;
    } else {
      formatted = `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(
        12
      )}`;
    }

    return formatted;
  };

  const handleCnicChange = (e) => {
    const rawValue = e.target.value;
    const formatted = formatCnic(rawValue);
    setCnic(formatted);
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (phone) {
      const digitsOnly = phone.replace(/\D/g, "");
      if (!/^92\d{10}$/.test(digitsOnly)) {
        newErrors.phone = "Phone number must be in the format +92 300-1234567";
        isValid = false;
      }
    }

    if (cnic) {
      const digits = cnic.replace(/\D/g, "");
      if (!/^\d{13}$/.test(digits)) {
        newErrors.cnic = "CNIC must be 13 digits (e.g., 33104-1234567-0)";
        isValid = false;
      }
    }

    if (error) {
      isValid = false;
    }

    setFormDataError(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const userData = new FormData();
    userData.append("name", formData.name);
    userData.append("address", formData.address);
    userData.append("gender", formData.gender);
    userData.append("phone", phone);
    userData.append("cnic", cnic);
    if (image?.file) {
      userData.append("profileImage", image?.file);
    }

    try {
      const response = await updateProfile(userData).unwrap();
      if (response.success) {
        toast.success(response.message);
        setIsOpen(false);
      }
    } catch (err) {
      console.error("users error:", err);
      showErrorToast(err);
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      email: "",
      address: "",
      gender: "",
    });
    setPhone("");
    setCnic("");
    setImage(null);
    setResetTrigger((prev) => prev + 1);
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-10 backdrop-blur-sm flex items-center justify-center z-[200]">
      <div className="relative bg-white/80 border border-white/30 backdrop-blur-lg rounded-2xl shadow-xl p-4 lg:w-[800px] w-[90%] max-h-[95vh] overflow-y-auto custom-scroll animate-fade-in text-black">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(false);
          }}
          className="absolute top-4 right-4 text-[#00643b]"
          aria-label="Close modal"
        >
          <FiX className="size-6" />
        </button>

        <h2 className="text-[1.5rem] font-bold lg:mb-4 mb-3 text-[#00643b]">
          Update Profile
        </h2>

        <form className="flex flex-col lg:gap-4 gap-3">
          <div className=" flex flex-col items-center gap-1">
            <div className="flex justify-center ">
              <div className="relative">
                {image ? (
                  <img
                    className="size-28  rounded-full border border-[#00643b]"
                    src={image.src}
                    alt="user_img"
                  />
                ) : (
                  <FaUserCircle className="size-28  rounded-full text-[#00643b]" />
                )}

                <label
                  htmlFor="editImg"
                  className="absolute right-2 bottom-0 rounded-full"
                >
                  <BiPencil className="text-2xl bg-white rounded-full p-1 text-[#00643b] transition-all ease-linear duration-300 cursor-pointer ring-1 ring-[#00643b]" />
                </label>
              </div>

              <div className="hidden">
                <input
                  type="file"
                  name="profile_img"
                  id="editImg"
                  onChange={(e) => handleFile(e.target.files[0])}
                  accept="image/*"
                />
              </div>
            </div>
            {error && (
              <p className=" md:text-sm text-[12px] text-red-500 ">{error}</p>
            )}
          </div>
          <div className=" grid sm:grid-cols-2 grid-cols-1 lg:gap-4 gap-3 text-sm font-medium">
            <div className=" flex flex-col gap-1">
              <label htmlFor="">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name"
                className="w-full border border-[#00000033] text-[#737791] outline-none  p-2 lg:rounded-xl rounded-lg"
              />
            </div>
            <div className=" flex flex-col gap-1">
              <label htmlFor="">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                placeholder="abcd@gmailcom"
                disabled
                className="w-full border border-[#00000033] text-[#737791] outline-none  p-2 lg:rounded-xl rounded-lg"
              />
            </div>
            <div className=" flex flex-col gap-1">
              <label htmlFor="">Phone</label>
              <input
                type="text"
                name="phone"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="+92 000-0000000"
                className="w-full border border-[#00000033] text-[#737791] outline-none  p-2 lg:rounded-xl rounded-lg"
              />
              {formDataError.phone && (
                <p className="text-red-500 text-sm mt-1">
                  {formDataError.phone}
                </p>
              )}
            </div>
            <div className=" flex flex-col gap-1">
              <label htmlFor="">CNIC</label>
              <input
                type="text"
                name="cnic"
                value={cnic}
                onChange={handleCnicChange}
                placeholder="33104-1234567-0"
                className="w-full border border-[#00000033] text-[#737791] outline-none  p-2 lg:rounded-xl rounded-lg"
              />
              {formDataError.cnic && (
                <p className="text-red-500 text-sm mt-1">
                  {formDataError.cnic}
                </p>
              )}
            </div>
            <div className=" flex flex-col gap-1">
              <label htmlFor="">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St, City, State, ZIP"
                className="w-full border border-[#00000033] text-[#737791] outline-none  p-2 lg:rounded-xl rounded-lg"
              />
            </div>
            <div className=" flex flex-col gap-1">
              <label htmlFor="">Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full border border-[#00000033] text-[#737791] outline-none  p-2 lg:rounded-xl rounded-lg"
              >
                <option value="" disabled>
                  Select the gender
                </option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>

          <div className=" w-full flex flex-col">
            <div className=" flex justify-end">
              <p
                onClick={() => handleReset()}
                className=" text-red-500 text-sm cursor-pointer underline"
              >
                Reset Form
              </p>
            </div>
            <button
              onClick={(e) => handleSubmit(e)}
              disabled={isLoading}
              className="bg-[#00643b] sm:w-[50%] w-max mx-auto text-white py-2 px-5 rounded-md mt-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center "
            >
              {isLoading ? (
                <ButtonLoading text="Updating Profile..." />
              ) : (
                "Update Profile"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateProfileForm;
